import { createSelector, MemoizedSelector, select } from '@ngrx/store';

import { StateSelector } from '@fcom/core/selectors/selector-utils';
import { HistogramBar } from '@fcom/common';
import { FinnairAirBoundsRequest } from '@fcom/dapi/api/models/finnair-air-bounds-request';
import { FinnairSearchParameters } from '@fcom/dapi/api/models/finnair-search-parameters';

import { BookingWidgetState } from '../../interfaces/store.interface';
import { CalendarPrices } from '../../interfaces/service.interface';
import { WidgetTab } from '../../interfaces/flight-search.interface';
import {
  BookingWidgetAndAppState,
  BookingWidgetAppState,
  bookingWidgetFeatureState,
} from './booking-widget-feature-state.selector';

const _bookingWidgetState = createSelector(
  bookingWidgetFeatureState,
  (_bs: BookingWidgetAppState) => _bs?.bookingWidget
);

const _fullYearPrices = createSelector(_bookingWidgetState, (bw: BookingWidgetState) => bw?.prices?.fullYear ?? {});
const _followingMonthPrices = createSelector(
  _bookingWidgetState,
  (bw: BookingWidgetState) => bw?.prices?.followingMonth ?? {}
);
const _histogramPrices = createSelector(_bookingWidgetState, (bw: BookingWidgetState) => bw?.prices?.histogram ?? []);
export const selectAirCalendarPrices = createSelector(
  _bookingWidgetState,
  (bw: BookingWidgetState) => bw?.prices?.airCalendar
);

export const selectNotificationWarnings = createSelector(
  _bookingWidgetState,
  (bw: BookingWidgetState) => bw?.warnings ?? {}
);

export const selectActiveTab = createSelector(
  _bookingWidgetState,
  (bw: BookingWidgetState) => bw?.activeTab ?? WidgetTab.FLIGHT
);

const sameCount = (a: number | undefined, b: number | undefined): boolean => (a ?? 0) === (b ?? 0);

const airCalendarSearchMatches = (search: FinnairSearchParameters, request: FinnairAirBoundsRequest): boolean =>
  !search.isAward &&
  search.locale === request.locale &&
  search.cabin === request.cabin &&
  (search.promoCode ?? '') === (request.promoCode ?? '') &&
  sameCount(search.adults, request.travelers.adults) &&
  sameCount(search.c15s, request.travelers.c15s) &&
  sameCount(search.children, request.travelers.children) &&
  sameCount(search.infants, request.travelers.infants) &&
  search.itinerary.length === request.itineraries.length &&
  search.itinerary.every(
    (bound, index) =>
      bound.departureLocationCode === request.itineraries[index].departureLocationCode &&
      bound.destinationLocationCode === request.itineraries[index].destinationLocationCode
  );

export const airCalendarPriceMatchSelector = (
  request: FinnairAirBoundsRequest
): MemoizedSelector<BookingWidgetAndAppState, string | undefined> =>
  createSelector(selectAirCalendarPrices, (airCalendar): string | undefined => {
    const search = airCalendar?.finnairSearchParameters;
    if (!search || !airCalendarSearchMatches(search, request)) {
      return undefined;
    }

    const [outbound, inbound] = request.itineraries;
    return airCalendar.airCalendars?.find(
      ({ departure, arrival }) =>
        departure === outbound.departureDate && (arrival || undefined) === inbound?.departureDate
    )?.totalPrice;
  });

export const fullYearPrices = (): StateSelector<BookingWidgetAppState, CalendarPrices> => select(_fullYearPrices);
export const followingMonthPrices = (): StateSelector<BookingWidgetAppState, CalendarPrices> =>
  select(_followingMonthPrices);
export const histogramPrices = (): StateSelector<BookingWidgetAppState, HistogramBar[]> => select(_histogramPrices);
